import { Box } from '@material-ui/core'
import { ChangeEvent, EventHandler, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddOrganizationModal } from '../addOrganization/AddOrganizationModal'

import { OrganisationAddressRow } from '@percent/admin-dashboard/api/types'
import { ErrorInfo, ErrorView, Table } from '@percent/admin-dashboard/common/components'
import {
  useFilterValue,
  useMutation,
  useOrganisationsAccess,
  useValidationRequestsAccess
} from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Button, FilterList, SearchInput, Spacer, useToast } from '@percent/lemonade'
import styles from './OrganizationsTable.module.scss'
import { useOrgTypesFilter } from './hooks/useOrgTypesFilter'
import { OrganizationsTableProps, UploadError } from './OrganizationsTable.types'
import { OrganizationsTableRow } from './OrganizationsTableRow'
import { UploadBatchOrganisationsUpdate } from './UploadBatchOrganisationsUpdate/UploadBatchOrganisationsUpdate'

export function OrganizationsTable({
  organisations,
  totalResults,
  nextPage,
  previousPage,
  isLoading,
  errorMessage,
  queryParams,
  setQueryParams,
  refresh
}: OrganizationsTableProps) {
  const { t } = useTranslation()
  const [dialogState, setDialogState] = useState(false)
  const [searchValue, setSearchValue] = useFilterValue<string>(queryParams.query ?? '', [queryParams.query])
  const addOrgFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_ADD_ORG)
  const { canBatchUpdate } = useOrganisationsAccess()
  const { adminService } = useServices()
  const [uploadError, setUploadError] = useState<UploadError | undefined>()
  const { typesOptions, onTypeChange, selectedTypes } = useOrgTypesFilter({ queryParams, setQueryParams })
  const { addToast } = useToast()
  // Adding an organisation creates a validation request, so it needs can_create on that
  // domain on top of the existing rollout flag.
  const { canCreate } = useValidationRequestsAccess()
  const canAddOrganisation = addOrgFeatureFlag && canCreate

  const handleRegistriesDialog = () => {
    setDialogState(prevState => !prevState)
  }

  const cells = [
    { id: 'country', isSortable: false },
    { id: 'name', isSortable: false, props: { width: '300px' } },
    { id: 'registryId', isSortable: false, props: { width: '115px' } },
    { id: 'claimed', isSortable: false },
    { id: 'type', isSortable: false },
    { id: 'registrationStatus', isSortable: false }
  ]

  const handleKeyChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    [setSearchValue]
  )

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value,
          page: 1
        })
      }

      if (event.key === 'Escape') {
        setSearchValue('')
      }

      return null
    },
    [setQueryParams, setSearchValue]
  )

  const handleClearValue = useCallback(() => {
    setSearchValue('')
  }, [setSearchValue])

  const [{ isLoading: isUploadInProgress }, { apiFunc: organisationsBatchUpdate }] = useMutation(
    adminService.commands.organisationsBatchUpdate,
    _response => {
      addToast(t('toast.organisationsBatchUpdateSuccess'), 'success')
      setQueryParams({
        page: undefined,
        query: undefined
      })
      refresh()
      setUploadError(undefined)
    },
    (e: any) => {
      if (e.response?.data?.error) {
        setUploadError(e.response?.data?.error)
      } else {
        addToast(t('toast.organisationsBatchUpdateError'), 'error')
      }
    }
  )

  const handleOrganisationsBatchUpdate = useCallback(
    async (payload: OrganisationAddressRow[]) => {
      await organisationsBatchUpdate({ payload })
    },
    [organisationsBatchUpdate]
  )

  const filters = useMemo(
    () => (
      <Box className={styles.wrapper}>
        <Box className={styles.filtersWrapper}>
          <SearchInput
            value={searchValue ?? ''}
            handleClearValue={handleClearValue}
            onKeyDown={handleKeyPress}
            onChange={handleKeyChange}
            placeholder={t('table.adminNonprofitsSearchPlaceholder')}
          />
          <Spacer size={4} axis="horizontal" />
          <FilterList
            label="Types"
            options={typesOptions}
            selectedValues={selectedTypes}
            onChange={onTypeChange}
            testId="org-type-filter"
          />
          <Spacer size={4} axis="horizontal" />
          {canAddOrganisation && (
            <Button variant="secondary" onClick={handleRegistriesDialog} data-testid="add-organisation-button">
              {t('button.addOrganization')}
            </Button>
          )}
          {canBatchUpdate && (
            <>
              <Spacer size={4} axis="horizontal" />
              <UploadBatchOrganisationsUpdate
                isUploadInProgress={isUploadInProgress}
                handleOrganisationsBatchUpdate={handleOrganisationsBatchUpdate}
              />
            </>
          )}
        </Box>
        {uploadError ? (
          <ErrorInfo
            header={`${t('typography.organisationsBatchUpdateErrorTitle')} ${uploadError.message}`}
            reasons={uploadError.reasons}
            footer={t('typography.organisationsBatchUpdateErrorInstruction')}
          />
        ) : null}
      </Box>
    ),
    [
      searchValue,
      handleClearValue,
      handleKeyPress,
      handleKeyChange,
      t,
      typesOptions,
      selectedTypes,
      onTypeChange,
      canAddOrganisation,
      canBatchUpdate,
      isUploadInProgress,
      handleOrganisationsBatchUpdate,
      uploadError
    ]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Box>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        data={organisations}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        orderBy="name"
        emptyTableText={t('table.emptyOrganizations')}
        filtersContent={filters}
      >
        {organisations?.map(row => (
          <OrganizationsTableRow key={row.id} row={row} />
        ))}
      </Table>
      {canAddOrganisation && (
        <AddOrganizationModal open={dialogState} onClose={handleRegistriesDialog} refresh={refresh} />
      )}
    </Box>
  )
}
